'use client';

import { c as _c } from "react/compiler-runtime";
import type { RichTextBlock, RichTextInline, RichTextLink, RichTextList, RichTextText } from '@repo/utils/rich-text';
import type { ReactNode } from 'react';
import { safeLinkHref } from '@repo/utils/rich-text';
import { createContext, useContext } from 'react';
import { cn } from '../../utils/cn';
import { Text } from '../typography/Text';
interface FormattedTextProps {
  node: RichTextText;
}
function FormattedText(t0) {
  const $ = _c(7);
  const {
    node
  } = t0;
  if (!node.bold && !node.italic && !node.underline) {
    return node.text;
  }
  const t1 = node.bold && "font-bold";
  const t2 = node.italic && "italic";
  const t3 = node.underline && "underline";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = cn(t1, t2, t3);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== node.text || $[5] !== t4) {
    t5 = <span className={t4}>{node.text}</span>;
    $[4] = node.text;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
export interface RichTextInternalLinkProps {
  href: string;
  rel: string;
  className: string;
  children: ReactNode;
}
const InternalLinkContext = createContext<((props: RichTextInternalLinkProps) => ReactNode) | null>(null);
export interface RichTextLinkProviderProps {
  /** Renders in-app paths, e.g. with `next/link`, so they navigate without a full reload. */
  renderInternalLink: (props: RichTextInternalLinkProps) => ReactNode;
  children: ReactNode;
}

/** Sets how the rich text renderers below it render in-app links. External links stay `<a>`. */
export function RichTextLinkProvider(t0) {
  const $ = _c(3);
  const {
    renderInternalLink,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== renderInternalLink) {
    t1 = <InternalLinkContext value={renderInternalLink}>{children}</InternalLinkContext>;
    $[0] = children;
    $[1] = renderInternalLink;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
interface InlineLinkProps {
  node: RichTextLink;
}

/** Re-checks the href so a hand-built model cannot render an unsafe link; a rejected href renders as text. */
function InlineLink(t0) {
  const $ = _c(4);
  const {
    node
  } = t0;
  const children = node.children.map(_temp);
  const renderInternalLink = useContext(InternalLinkContext);
  const href = safeLinkHref(node.href);
  if (!href) {
    return children;
  }
  const internal = href.startsWith("/");
  if (internal && renderInternalLink) {
    return renderInternalLink({
      href,
      rel: "noopener noreferrer",
      className: "text-foreground underline underline-offset-2 hover:opacity-80",
      children
    });
  }
  const t1 = internal ? "_self" : "_blank";
  let t2;
  if ($[0] !== children || $[1] !== href || $[2] !== t1) {
    t2 = <a href={href} target={t1} rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:opacity-80">{children}</a>;
    $[0] = children;
    $[1] = href;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(child, index) {
  return <FormattedText key={index} node={child} />;
}
export interface InlineRichTextProps {
  content: readonly RichTextInline[];
}

/** Renders validated inline rich text from `@repo/utils/rich-text`: bold, italic and underline marks and links. */
export function InlineRichText({
  content
}: InlineRichTextProps) {
  return content.map((node, index) => node.type === 'link' ? <InlineLink key={index} node={node} /> : <FormattedText key={index} node={node} />);
}
interface RichTextListViewProps {
  list: RichTextList;
}
function RichTextListView(t0) {
  const $ = _c(8);
  const {
    list
  } = t0;
  const List = list.ordered ? "ol" : "ul";
  let t1;
  if ($[0] !== list.items || $[1] !== list.ordered) {
    let t2;
    if ($[3] !== list.ordered) {
      t2 = (item, index) => <li key={index} className="flex items-baseline gap-3"><Text size="2xs" tag="span" aria-hidden={true} className={cn("text-muted-foreground shrink-0", !list.ordered && "relative top-[-0.1em] text-[0.6em] leading-none")}>{list.ordered ? `${index + 1}.` : "\u25CF"}</Text><div className="flex min-w-0 flex-col gap-1"><Text size="xs" tag="span"><InlineRichText content={item.content} /></Text>{item.children.map(_temp2)}</div></li>;
      $[3] = list.ordered;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = list.items.map(t2);
    $[0] = list.items;
    $[1] = list.ordered;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== List || $[6] !== t1) {
    t2 = <List role="list" className="text-foreground flex list-none flex-col gap-1">{t1}</List>;
    $[5] = List;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp2(child, childIndex) {
  return <RichTextListView key={childIndex} list={child} />;
}
export interface RichTextProps {
  blocks: readonly RichTextBlock[];
  className?: string;
}

/** Renders validated rich text blocks from `@repo/utils/rich-text`. Never accepts raw CMS JSON. */
export function RichText(t0) {
  const $ = _c(7);
  const {
    blocks,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("text-foreground flex flex-col gap-3 text-xs whitespace-pre-line", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== blocks) {
    t2 = blocks.map(_temp3);
    $[2] = blocks;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp3(block, index) {
  switch (block.type) {
    case "heading":
      {
        const Heading = block.level;
        return <Heading key={index} className="text-popover-foreground mt-2 text-xs font-semibold"><InlineRichText content={block.content} /></Heading>;
      }
    case "paragraph":
      {
        return <Text key={index} size="xs"><InlineRichText content={block.content} /></Text>;
      }
    case "quote":
      {
        return <blockquote key={index} className="border-border text-muted-foreground border-l-2 pl-3"><InlineRichText content={block.content} /></blockquote>;
      }
    case "list":
      {
        return <RichTextListView key={index} list={block} />;
      }
  }
}